import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, switchMap, catchError, throwError } from 'rxjs';
import { AuthenticationService } from '@shared/services';

@Injectable()
export class HttpBackendInterceptor implements HttpInterceptor {
  /** Injection of {@link AuthenticationService}. */
  private readonly authService = inject(AuthenticationService);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.shouldSkipAuthorization(req)) {
      return next.handle(req);
    }

    if (!this.authService.hasValidAccessToken()) {
      return this.handleRefreshAndRetry(req, next);
    }

    const modifiedReq = this.addAuthorizationHeader(
      req,
      this.authService.access_token
    );

    return next.handle(modifiedReq).pipe(
      catchError(error => {
        if (error.status !== 401) {
          return throwError(() => error);
        }
        return this.handleRefreshAndRetry(req, next);
      })
    );
  }

  private addAuthorizationHeader(
    req: HttpRequest<unknown>,
    token?: string | null
  ): HttpRequest<unknown> {
    if (!token) {
      return req;
    }

    const headers = new HttpHeaders().append(
      'Authorization',
      `Bearer ${token}`
    );
    return req.clone({ headers, url: req.url });
  }

  private handleRefreshAndRetry(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return this.authService.refreshToken().pipe(
      switchMap((token: string) => {
        const modifiedReq = this.addAuthorizationHeader(req, token);
        return next.handle(modifiedReq);
      }),
      catchError(error => {
        this.authService.logout();
        return throwError(() => error);
      })
    );
  }

  private shouldSkipAuthorization(req: HttpRequest<unknown>): boolean {
    return (
      req.url.endsWith('/token/') ||
      req.url.endsWith('/reset-password-request/') ||
      req.url.endsWith('/verify-email-token/') ||
      req.url.endsWith('/verify-reset-password-token/') ||
      (req.url.endsWith('/users/') && req.method === 'POST') ||
      (req.url.endsWith('/v1/embassies/') && req.method === 'GET') ||
      (req.url.endsWith('/v1/events/**') && req.method === 'GET') ||
      req.url.startsWith('https://maps.google.com/maps/api/geocode') ||
      req.url.includes('.eglisemlk.fr/donation-campaign/') ||
      req.url.includes('.eglisemlk.fr/stripe/checkout-session/') ||
      req.url.includes('.eglisemlk.fr/checkout-session/') ||
      req.url.includes('assets/icons/')
    );
  }
}
